import {
  BlotterDensity,
  BlotterTable,
  Box,
  type Column,
  type ColumnDef,
  getBlotterHeaderAndCellPadding,
  getSpreadPctDisplay,
  HStack,
  isPricingModeRelative,
  LoaderSizes,
  LoaderTalos,
  type NumericField,
  PricingModeEnum,
  type SizeColumnParams,
  type SpreadColumnParams,
  Stack,
  Text,
  Tooltip,
  useBlotterTable,
  useCallbackRef,
  useObservableValue,
  usePersistedBlotterTable,
} from '@talos/kyoko';
import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { useMemo } from 'react';
import type { Observable } from 'rxjs';
import { useTheme } from 'styled-components';
import { useTradingSettings } from '../../../../providers/AppConfigProvider';
import { useAppStateDispatch, useAppStateSelector } from '../../../../providers/AppStateProvider';
import { useManualRFQPricingContext } from '../ManualRFQPricingContextProvider';
import { useCompositePricePreview } from '../useCompositePricePreview';
import { PricingPxNumberInput } from './PricingPxNumberInput';
import { PricingQtyNumberInput } from './PricingQtyNumberInput';
import { usePricingRowsObs } from './stateToPricingRows';
import type { PricingRow } from './types';
import { usePricingBlotterColumnWidths } from './usePricingBlotterColumnWidths';

const RFQ_PRICING_PANEL_BLOTTER_ID = 'rfq-pricing-panel-blotter';
const RFQ_PRICING_PANEL_BLOTTER_ID_SIZE_COUNTERABLE = 'rfq-pricing-panel-blotter-size-counterable';

export const PRICING_PANEL_BLOTTER_DENSITY: BlotterDensity = BlotterDensity.Comfortable;
const LEG_BID_QTY_COL_ID = 'leg-bid-qty';
const LEG_OFFER_QTY_COL_ID = 'leg-offer-qty';

interface PricingPanelBlotterProps {
  sizeCounteringEnabled: boolean;
}

export const PricingPanelBlotter = ({ sizeCounteringEnabled }: PricingPanelBlotterProps) => {
  const dataObservable = usePricingRowsObs();
  const columns = usePricingPanelBlotterColumns(sizeCounteringEnabled);
  const { showAllDecimalsInBlotters, rfqPricingPanelShowAllDecimals } = useTradingSettings();

  const { onColumnSizeChange, colWidthMapObs } = usePricingBlotterColumnWidths();
  const blotterSettings = useMemo(
    () => ({
      showAllDecimals: showAllDecimalsInBlotters || rfqPricingPanelShowAllDecimals,
    }),
    [showAllDecimalsInBlotters, rfqPricingPanelShowAllDecimals]
  );

  // Size countering puts different columns into view. Enough to warrant two different persisted blotters so we store things such as preferred
  // column width per mode. This is good for now. Might just get rid of it though, honestly. Will wait for feedback / complaints.
  const blotterID = sizeCounteringEnabled
    ? RFQ_PRICING_PANEL_BLOTTER_ID_SIZE_COUNTERABLE
    : RFQ_PRICING_PANEL_BLOTTER_ID;
  const persistence = usePersistedBlotterTable(blotterID);

  const blotterTable = useBlotterTable({
    dataObservable,
    errorObservable: undefined,
    rowID: 'Symbol' satisfies keyof PricingRow,
    columns,
    persistence,
    density: PRICING_PANEL_BLOTTER_DENSITY,
    settings: blotterSettings,
    applyTransactionsSynchronously: true,
    gridOptions: {
      suppressHeaderFocus: true,
      focusGridInnerElement: params => {
        // Called when focus is going into the blotter
        // Can either be "from the top" or "from the bottom" (tab vs shift+tab)
        // We resolve the correct input element and manually call focus on it on behalf of aggrid
        let rowCount = 0;
        params.api.forEachNode(() => rowCount++);
        const rowIndexToFocus = params.fromBottom ? rowCount - 1 : 0;
        const sideToFocus = params.fromBottom ? 'ask' : 'bid';
        const inputElement = document.getElementById(
          pricingPanelInputID(rowIndexToFocus, sideToFocus)
        ) as HTMLInputElement | null;
        if (inputElement) {
          inputElement.focus();
          inputElement.select(); // selects all text, needs to be after focus
        }

        return true; // tells aggrid that we took care of the focusing ourselves
      },
      // These are the 4 identified events we need to listen to to ensure we are keeping our colWidthMap in sync with aggrid's col widths
      onGridSizeChanged: onColumnSizeChange,
      onColumnVisible: onColumnSizeChange,
      onColumnResized: onColumnSizeChange,
      onGridReady: onColumnSizeChange,
    },
  });

  return (
    <Stack h="100%" gap="spacing2">
      <CompositeBidAskRow colWidthMapObs={colWidthMapObs} sizeCounteringEnabled={sizeCounteringEnabled} />
      <Box h="100%">
        <BlotterTable {...blotterTable} />
      </Box>
    </Stack>
  );
};

/**
 * This component re-renders a bunch due to subscribing to colWidthMapObs. It listens to these column widths, and
 * maps these column widths to internal hstack member widths. These lets us line up components in this hstack with
 * the components in the blotter rows perfectly
 */
const CompositeBidAskRow = ({
  colWidthMapObs,
  sizeCounteringEnabled,
}: {
  colWidthMapObs: Observable<Map<string, number>>;
  sizeCounteringEnabled: boolean;
}) => {
  const { selectors, actions } = useManualRFQPricingContext();
  const state = useAppStateSelector(selectors.selectState);
  const { bid, ask } = state.form.compositeBidAsk;

  const compositeSpreadError = useAppStateSelector(selectors.selectCompositeSpreadError);
  const bidQtyError = useAppStateSelector(selectors.selectBidQtyError);
  const askQtyError = useAppStateSelector(selectors.selectAskQtyError);
  const widths = useObservableValue(() => colWidthMapObs, [colWidthMapObs]);
  const dispatch = useAppStateDispatch();

  function getWidthForColIDs(keys: string[]): number {
    return keys.reduce((total, key) => total + (widths?.get(key) ?? 100), 0); // default to 100 :shrug:
  }

  const theme = useTheme();
  const blotterPaddingForDensity = getBlotterHeaderAndCellPadding(theme, PRICING_PANEL_BLOTTER_DENSITY) + 'px';

  const isRelativePrice = isPricingModeRelative(state.currentCustomerQuote?.PricingMode);
  const numberInputSuffix = isRelativePrice ? 'BPS' : state.currentCustomerQuote?.AmountCurrency;
  const qtyInputSuffix = 'Units'; // multitoken top-level order qty is always in units
  const { preview, fetching } = useCompositePricePreview();

  const spread = useMemo(() => {
    if (bid.value && ask.value) {
      return getSpreadPctDisplay(bid.value, ask.value, { isRelativePrice });
    }

    if (preview?.spread) {
      return preview.spread;
    }

    return undefined;
  }, [bid, ask, preview, isRelativePrice]);

  return (
    <HStack background="colors.gray.010" w="100%" h="48px" justifyContent="flex-start" py="spacing8">
      <Box w={getWidthForColIDs(['Symbol', 'fixingIndex', 'quantity'])} pl={blotterPaddingForDensity}>
        <Text textTransform="uppercase" fontSize="fontSizeDefault" color="colorTextSubtle">
          Total Composite
        </Text>
      </Box>

      {widths != null && (
        <>
          {sizeCounteringEnabled && (
            <Box w={getWidthForColIDs([LEG_BID_QTY_COL_ID])} px={blotterPaddingForDensity}>
              <PricingQtyNumberInput
                field={state.form.compositeBidAsk.bidQty}
                suffix={qtyInputSuffix}
                minIncrement={state.currentCustomerQuote?.MinSizeIncrement}
                errorString={bidQtyError}
                invalid={bidQtyError != null}
                onChange={value => dispatch(actions.setCompositeBidQty(value))}
                data-testid="pricing-panel-composite-bid-qty"
              />
            </Box>
          )}

          <Box w={getWidthForColIDs(['leg-bid'])} px={blotterPaddingForDensity}>
            <PricingPxNumberInput
              field={bid}
              type="bid"
              suffix={numberInputSuffix}
              onChange={value => dispatch(actions.setCompositeBid(value))}
              data-testid="pricing-panel-composite-bid"
              autoFocus // when the panel is opened, we want to automatically put focus on this input
              placeholder={preview?.bidDisplay}
            />
          </Box>

          <Box
            w={getWidthForColIDs(['spread'])}
            textAlign="center"
            color={compositeSpreadError ? 'colorTextNegative' : 'colorTextDefault'}
            data-testid="pricing-panel-composite-spread"
          >
            <Tooltip tooltip={compositeSpreadError}>
              <Text color={compositeSpreadError ? 'colorTextNegative' : 'colorTextDefault'}>{spread ?? '-'}</Text>
            </Tooltip>
          </Box>

          <Box w={getWidthForColIDs(['leg-ask'])} px={blotterPaddingForDensity}>
            <PricingPxNumberInput
              field={ask}
              type="ask"
              suffix={numberInputSuffix}
              onChange={value => dispatch(actions.setCompositeAsk(value))}
              data-testid="pricing-panel-composite-ask"
              placeholder={preview?.offerDisplay}
            />
          </Box>

          {sizeCounteringEnabled && (
            <Box w={getWidthForColIDs([LEG_OFFER_QTY_COL_ID])} px={blotterPaddingForDensity}>
              <PricingQtyNumberInput
                field={state.form.compositeBidAsk.askQty}
                suffix={qtyInputSuffix}
                minIncrement={state.currentCustomerQuote?.MinSizeIncrement}
                errorString={askQtyError}
                invalid={askQtyError != null}
                onChange={value => dispatch(actions.setCompositeAskQty(value))}
                data-testid="pricing-panel-composite-offer-qty"
              />
            </Box>
          )}

          <Box>{fetching && <LoaderTalos size={LoaderSizes.XS} />}</Box>
        </>
      )}
    </HStack>
  );
};

const usePricingPanelBlotterColumns = (sizeCounteringEnabled: boolean): Column[] => {
  const { actions } = useManualRFQPricingContext();
  const dispatch = useAppStateDispatch();

  const handleLegBidAskChange = useCallbackRef((type: 'bid' | 'ask', value: string, symbol: string) => {
    const action = type === 'bid' ? actions.setLegBid : actions.setLegAsk;
    dispatch(action({ value, symbol }));
  });

  return useMemo(() => {
    return [
      {
        type: 'security',
        field: 'Symbol',
        params: {
          colorful: true,
        },
        suppressKeyboardEvent: () => true,
      },
      {
        type: 'fixingIndex',
        id: 'fixingIndex',
        field: 'FixingDetails.Index',
        width: 100,
        title: 'Index',
        suppressKeyboardEvent: () => true,
      },
      {
        type: 'size',
        field: 'quantity',
        title: 'Quote Qty',
        width: 180,
        params: {
          currencyField: 'Currency',
        } satisfies SizeColumnParams<PricingRow>,
        suppressKeyboardEvent: () => true,
      },
      ...(sizeCounteringEnabled ? [getLegQtyColumn('bid')] : []),
      getLegBidAskColumn('bid', (value, symbol) => handleLegBidAskChange('bid', value, symbol)),
      {
        type: 'spread',
        id: 'spread',
        title: 'spread',
        field: 'spread',
        params: {
          showBPSIfRelevant: false,
          highlightNegativeSpread: true,
        } satisfies SpreadColumnParams<PricingRow>,
        suppressKeyboardEvent: () => true,
      },
      getLegBidAskColumn('ask', (value, symbol) => handleLegBidAskChange('ask', value, symbol)),
      ...(sizeCounteringEnabled ? [getLegQtyColumn('offer')] : []),
      {
        type: 'indexMark',
        field: 'indexMark',
        title: 'Prev Day Mark',
        description: 'Shows the Index Mark for the day before the trade date',
        params: {
          useFixingIncrement: true,
        },
      },
    ] satisfies ColumnDef<PricingRow>[];
  }, [handleLegBidAskChange, sizeCounteringEnabled]);
};

function getLegQtyColumn(type: 'bid' | 'offer'): ColumnDef<PricingRow> {
  return {
    type: 'size',
    id: type === 'bid' ? LEG_BID_QTY_COL_ID : LEG_OFFER_QTY_COL_ID,
    field: type === 'bid' ? 'bidQty' : 'offerQty',
    title: type === 'bid' ? 'Bid Qty' : 'Offer Qty',
    initialWidth: 180,
    params: {
      currencyField: 'Currency',
    } satisfies SizeColumnParams<PricingRow>,
    suppressKeyboardEvent: () => true,
  };
}

type LegBidAskColumnValue =
  | {
      field: NumericField;
      symbol: string;
      pricingMode: PricingModeEnum;
      currency: string | undefined;
      placeholder: string | undefined;
    }
  | undefined;

function getLegBidAskColumn(
  type: 'bid' | 'ask',
  onChange: (value: string, symbol: string) => void
): ColumnDef<PricingRow> {
  return {
    type: 'custom',
    id: `leg-${type}`,
    sortable: false,
    title: type === 'bid' ? 'Customer Sell' : 'Customer Buy',
    headerClass: 'ag-right-aligned-cell', // input text is right-aligned, feels better to right-align
    params: {
      /** Before this was true, things such as arrow keys didnt work inside the number inputs we render in this cell */
      suppressKeyboardEvent: () => true,
      valueGetter: (params: ValueGetterParams<PricingRow>): LegBidAskColumnValue => {
        const field = type === 'bid' ? params.data?.bid : params.data?.ask;
        const placeholder = type === 'bid' ? params.data?.bidPlaceholder : params.data?.askPlaceholder;
        const symbol = params.data?.Symbol;
        const pricingMode = params.data?.pricingMode;

        const currency = params.data?.amountCurrency;

        if (!field || !symbol || !pricingMode) {
          return undefined;
        }

        return {
          field,
          symbol,
          pricingMode,
          currency,
          placeholder,
        };
      },
      cellRenderer: ({ value, node }: ICellRendererParams<PricingRow, LegBidAskColumnValue>) => {
        if (!value) {
          return <></>;
        }

        const { field, symbol, pricingMode, currency } = value;
        const pricingInBps = pricingMode === PricingModeEnum.SpreadToFixing;
        const suffix = pricingInBps ? 'BPS' : currency;

        return (
          <PricingPxNumberInput
            field={field}
            type={type}
            onChange={value => onChange(value, symbol)}
            suffix={suffix}
            id={pricingPanelInputID(node.rowIndex, type)}
            data-testid={`pricing-panel-leg-${symbol}-${type}`}
            placeholder={value.placeholder}
          />
        );
      },
    } satisfies ColDef<PricingRow>,
  };
}

function pricingPanelInputID(rowIndex: number | null, type: 'bid' | 'ask'): string {
  return `pricing-panel-input-${rowIndex}-${type}`;
}
